import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

type EnumMultiSelectProps = {
  name: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
};

function parseSelected(value: string): string[] {
  return value
    ? value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
    : [];
}

export default function EnumMultiSelect({ name, options, value, onChange }: EnumMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = parseSelected(value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(option => option.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (option: string) => {
    const next = selected.includes(option) ? selected.filter(item => item !== option) : [...selected, option];
    onChange(next.join(','));
  };

  return (
    <div className="api-console-ms" ref={wrapRef}>
      <div
        className={`api-console-ms-trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen(v => !v)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(v => !v);
          }
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-multiselectable="true"
        aria-label={name}
        tabIndex={0}
      >
        <div className="api-console-ms-chips">
          {selected.length === 0 ? (
            <span className="api-console-ms-placeholder">Select one or more…</span>
          ) : (
            selected.map(item => (
              <span key={item} className="api-console-ms-chip">
                {item}
                <button
                  type="button"
                  className="api-console-ms-chip-remove"
                  aria-label={`Remove ${item}`}
                  onClick={event => {
                    event.stopPropagation();
                    toggle(item);
                  }}
                >
                  <XMarkIcon />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronDownIcon className="api-console-ms-caret" />
      </div>

      {open && (
        <div className="api-console-ms-panel" role="listbox" aria-label={`${name} options`}>
          {options.length > 8 && (
            <div className="api-console-ms-search">
              <MagnifyingGlassIcon />
              <input
                ref={searchRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search values"
                aria-label={`Search ${name}`}
                onClick={event => event.stopPropagation()}
              />
            </div>
          )}
          <ul className="api-console-ms-list">
            {filtered.map(option => {
              const isOn = selected.includes(option);
              return (
                <li key={option}>
                  <label className={`api-console-ms-option${isOn ? ' is-on' : ''}`}>
                    <input type="checkbox" checked={isOn} onChange={() => toggle(option)} />
                    <span>{option}</span>
                  </label>
                </li>
              );
            })}
            {filtered.length === 0 && <li className="api-console-ms-empty">No matches</li>}
          </ul>
          {selected.length > 0 && (
            <button type="button" className="api-console-ms-clear" onClick={() => onChange('')}>
              Clear all ({selected.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}
